import { useState } from "react";
import { useDebounce } from "use-debounce";

import { Column, Row, Text } from "@/components";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Command, CommandItem, CommandList } from "@ui/command";
import { Input } from "@ui/input";

import { useGetPlaces } from "@/hooks/queries/useGetPlaces";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useUpdateSetCandidacyCommute } from "@/hooks/updateSets/useUpdateSetCandidacyCommute";
import { useUpdateSetLocation } from "@/hooks/updateSets/useUpdateSetLocation";
import { userSheetViewStore } from "@/stores/userSheetView";

const LOCATION_QUERY_DEBOUNCE_MS = 250;

// A places search, matching the sheet-header location editor: free text only
// queries the list, and picking a place is what saves. That keeps every stored
// location a real, geocodable one.
export const LocationEditor = ({
  candidacyId,
  onDone,
  stafferId,
  userId,
}: {
  candidacyId: string | null;
  onDone: () => void;
  stafferId: number;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const setIsCommuteRefreshing = userSheetViewStore(
    (s) => s.setIsCommuteRefreshing,
  );
  // Update
  const { isPending, mutate: saveLocation } = useUpdateSetLocation();
  const { mutate: recomputeCommute } = useUpdateSetCandidacyCommute();

  /* Cache Updates */
  const setCandidacyInAllLists = useSetCandidacyInAllLists();

  /* React Hooks */
  const [locationQuery, setLocationQuery] = useState("");
  const [debouncedLocationQuery] = useDebounce(
    locationQuery,
    LOCATION_QUERY_DEBOUNCE_MS,
  );
  const placesData = useGetPlaces(debouncedLocationQuery).data;

  /* Handlers */
  // The commute figures on this row derive from the location, so a location
  // save also recomputes them; the flag keeps the header metric spinning until
  // the fresh values land.
  const handleRecomputeCommute = (selectedLocation: string) => {
    if (!candidacyId) return;
    // The hook clears the flag on settle; clearing it here would be skipped,
    // since this editor unmounts (onDone) before the recompute resolves.
    setIsCommuteRefreshing(true);
    recomputeCommute({ candidacyId, location: selectedLocation });
    setCandidacyInAllLists({ candidacyId, location: selectedLocation });
  };

  const handleLocationSelect = (selectedLocation: string) => {
    saveLocation(
      { data: { location: selectedLocation }, stafferId, userId },
      {
        onSuccess: () => {
          handleRecomputeCommute(selectedLocation);
          onDone();
        },
      },
    );
  };

  /* Render */
  const renderPlacesList = () => {
    if (!locationQuery || !placesData?.length) return null;
    return (
      <Command>
        <CommandList className="absolute z-10 w-full rounded-md border border-slate-200 bg-white shadow-lg">
          {placesData.map((place) => (
            <CommandItem
              className="cursor-pointer px-3 py-2"
              key={place}
              onSelect={() => {
                handleLocationSelect(place);
              }}
            >
              {place}
            </CommandItem>
          ))}
        </CommandList>
      </Command>
    );
  };

  const render = () => {
    if (isPending) {
      return (
        <Row className="h-7 items-center gap-1.5">
          <LoadingIndicator variant="small" />
          <Text className="text-sm font-normal text-slate-500">Saving</Text>
        </Row>
      );
    }

    return (
      <Column className="relative w-60 gap-0">
        <Input
          autoFocus
          className="h-7 text-sm"
          onChange={(event) => {
            setLocationQuery(event.target.value);
          }}
          placeholder="Begin typing a location..."
          value={locationQuery}
        />
        {renderPlacesList()}
      </Column>
    );
  };

  return render();
};
